import React, { useState, useEffect } from 'react';
import { Box, Button, Input, Table, Thead, Tbody, Tr, Th, Td, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure, Flex, Heading, Text, FormControl, FormLabel, IconButton, useColorModeValue, useToast, Spinner, Badge, Tooltip, HStack, Select } from '@chakra-ui/react';
import { FaTrash, FaCopy } from 'react-icons/fa';
import { RepeatIcon, WarningTwoIcon } from '@chakra-ui/icons';
import client from '../utils/fetchClient';
import useDebouncedValue from '../utils/useDebouncedValue';

function ApiKeyModal({ open, onClose, onSave, loading }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open) setForm({ name: '' });
  }, [open]);
  const modalBg = useColorModeValue('white', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.600');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');
  return (
    <Modal isOpen={open} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader>New API Key</ModalHeader>
        <ModalBody pb={6}>
          <FormControl mb={4} isRequired>
            <FormLabel>Key Name</FormLabel>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Enter key name" bg={inputBg} borderColor={inputBorder} />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} mr={3} borderRadius="lg">Cancel</Button>
          <Button colorScheme="green" onClick={() => onSave(form)} borderRadius="lg" isLoading={loading}>Create</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function SettingsApiKeysTab({ readOnly = false }) {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { isOpen: modalOpen, onOpen, onClose } = useDisclosure();
  const [creating, setCreating] = useState(false);
  const [showKey, setShowKey] = useState(null); // { apiKey, keyPrefix, id }
  const { isOpen: revokeOpen, onOpen: onOpenRevoke, onClose: onCloseRevoke } = useDisclosure();
  const { isOpen: regenOpen, onOpen: onOpenRegen, onClose: onCloseRegen } = useDisclosure();
  const [targetKey, setTargetKey] = useState(null); // row for revoke/regenerate
  const [processing, setProcessing] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const toast = useToast();

  const textColor = useColorModeValue('gray.800', 'gray.100');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('green.50', 'gray.700');
  const tableHeaderColor = useColorModeValue('green.700', 'green.200');
  const tableRowBgEven = useColorModeValue('white', 'gray.800');
  const tableRowBgOdd = useColorModeValue('gray.50', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');

  // Fetch API keys from backend
  const fetchApiKeys = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await client.get('/api/keys');
      if (!res.ok) throw new Error('Failed to fetch API keys');
      const result = await res.json();
      setData(result.keys || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  // Create new API key
  const handleAdd = async (form) => {
    setError('');
    setCreating(true);
    try {
      const res = await client.post('/api/keys', { body: { name: form.name } });
      if (!res.ok) throw new Error('Failed to create API key');
      const result = await res.json();
      setShowKey(result); // { apiKey, keyPrefix, id }
      onClose();
      fetchApiKeys();
      toast({
        title: 'API Key Created.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  // Revoke API key
  const handleDelete = async (id) => {
    setError('');
    try {
      const res = await client.delete(`/api/keys/${id}`);
      if (!res.ok) throw new Error('Failed to revoke API key');
      fetchApiKeys();
      toast({
        title: 'API Key Revoked.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      setError(e.message);
    }
  };

  // Regenerate API key
  const handleRegenerate = async (id) => {
    setError('');
    setProcessing(true);
    try {
      const res = await client.post(`/api/keys/${id}/regenerate`);
      if (!res.ok) throw new Error('Failed to regenerate API key');
      const result = await res.json();
      setShowKey(result);
      fetchApiKeys();
      toast({ title: 'API Key regenerated.', status: 'success', duration: 3000, isClosable: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleCopy = (key) => {
    navigator.clipboard.writeText(key);
    toast({
      title: 'API Key copied.',
      status: 'info',
      duration: 1500,
      isClosable: true,
    });
  };

  const debouncedSearch = useDebouncedValue(search, 300);
  const filtered = data.filter(row =>
    (row.keyPrefix || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    (row.status || '').toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const startIdx = (page - 1) * pageSize;
  const pageRows = filtered.slice(startIdx, startIdx + pageSize);

  return (
    <Box>
      <Heading as="h3" size="md" mb={2} color={textColor}>API Keys</Heading>
      <Text fontSize="sm" color="gray.500" mb={6}>Manage API keys for integrations and automation.</Text>
      <Flex align="center" mb={6} justify="space-between" flexWrap="wrap" gap={4}>
        <Flex gap={2}>
          <Button colorScheme="green" onClick={fetchApiKeys} size="sm" borderRadius="lg">Refresh</Button>
          {!readOnly && (
            <Button colorScheme="green" onClick={onOpen} size="sm" borderRadius="lg">Create key</Button>
          )}
        </Flex>
        <HStack>
          <Input
            placeholder="Search by prefix or status"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            size="sm"
            borderRadius="full"
            maxW="220px"
            minW="120px"
            bg={inputBg}
            borderColor={inputBorder}
          />
          <Select size="sm" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </Select>
        </HStack>
      </Flex>
      {error && <Text color="red.500" mb={2}>{error}</Text>}
      {loading ? (
        <Flex align="center" justify="center" minH="120px"><Spinner /></Flex>
      ) : filtered.length === 0 ? (
        <Box borderWidth={1} borderRadius="lg" bg={cardBg} borderColor={borderColor} p={8} textAlign="center">
          <Heading size="sm" mb={2}>No API keys</Heading>
          <Text color="gray.500" mb={4}>Create your first key to integrate with external systems.</Text>
          <Button colorScheme="green" onClick={onOpen}>New API Key</Button>
        </Box>
      ) : (
        <Box borderWidth={1} borderRadius="lg" bg={cardBg} borderColor={borderColor} overflowX="auto">
          <Table size="sm">
            <Thead bg={tableHeaderBg} color={tableHeaderColor}>
              <Tr>
                <Th>Prefix</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Expires</Th>
                <Th>Last Used</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pageRows.map((row, idx) => (
                <Tr key={row.id} bg={idx % 2 === 0 ? tableRowBgEven : tableRowBgOdd}>
                  <Td>{row.keyPrefix}</Td>
                  <Td><Badge colorScheme={row.status === 'active' ? 'green' : 'red'}>{row.status}</Badge></Td>
                  <Td>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-'}</Td>
                  <Td>{row.expiresAt ? new Date(row.expiresAt).toLocaleDateString() : '-'}</Td>
                  <Td>{row.lastUsedAt ? new Date(row.lastUsedAt).toLocaleDateString() : '-'}</Td>
                  <Td>
                    {readOnly ? (
                      <Text fontSize="xs" color="gray.400">View only</Text>
                    ) : (
                      <HStack>
                        <Tooltip label="Copy prefix" hasArrow><IconButton icon={<FaCopy />} aria-label="Copy prefix" size="xs" onClick={() => handleCopy(row.keyPrefix)} /></Tooltip>
                        <Tooltip label="Regenerate" hasArrow><IconButton icon={<RepeatIcon />} aria-label="Regenerate" size="xs" onClick={() => { setTargetKey(row); onOpenRegen(); }} /></Tooltip>
                        <Tooltip label="Revoke" hasArrow><IconButton icon={<FaTrash />} aria-label="Revoke" size="xs" colorScheme="red" onClick={() => { setTargetKey(row); onOpenRevoke(); }} /></Tooltip>
                      </HStack>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      {/* Pagination */}
      {filtered.length > 0 && (
        <HStack mt={4} justify="space-between">
          <Text fontSize="sm" color="gray.500">Page {page} of {totalPages}</Text>
          <HStack>
            <Button size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page === 1}>Previous</Button>
            <Button size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} isDisabled={page === totalPages}>Next</Button>
          </HStack>
        </HStack>
      )}
      <ApiKeyModal open={modalOpen} onClose={onClose} onSave={handleAdd} loading={creating} />
      {showKey && (
        <Modal isOpen={!!showKey} onClose={() => setShowKey(null)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>API Key Created</ModalHeader>
            <ModalBody>
              <Text fontWeight="bold" mb={2}>Copy and store this API key securely. You will not be able to see it again.</Text>
              <Flex align="center" gap={2} mb={2}>
                <Input value={showKey.apiKey} isReadOnly size="sm" />
                <IconButton icon={<FaCopy />} aria-label="Copy Key" size="sm" onClick={() => handleCopy(showKey.apiKey)} />
              </Flex>
              <Text fontSize="sm" color="gray.500">Prefix: {showKey.keyPrefix}</Text>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setShowKey(null)} colorScheme="green">Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      {/* Confirm Revoke */}
      <Modal isOpen={revokeOpen} onClose={onCloseRevoke} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Revoke API Key</ModalHeader>
          <ModalBody>
            <Flex align="center" gap={3} mb={2}><WarningTwoIcon color="red.400" /><Text>Are you sure you want to revoke this key? This action cannot be undone.</Text></Flex>
            <Text fontSize="sm" color="gray.500">Prefix: {targetKey?.keyPrefix}</Text>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onCloseRevoke}>Cancel</Button>
            <Button colorScheme="red" isLoading={processing} onClick={async () => { setProcessing(true); await handleDelete(targetKey.id); setProcessing(false); onCloseRevoke(); }}>Revoke</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Confirm Regenerate */}
      <Modal isOpen={regenOpen} onClose={onCloseRegen} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Regenerate API Key</ModalHeader>
          <ModalBody>
            <Text mb={2}>Regenerating creates a new secret for this key. Existing clients using the old secret will stop working.</Text>
            <Text fontSize="sm" color="gray.500">Prefix: {targetKey?.keyPrefix}</Text>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onCloseRegen}>Cancel</Button>
            <Button colorScheme="blue" isLoading={processing} onClick={async () => { setProcessing(true); await handleRegenerate(targetKey.id); setProcessing(false); onCloseRegen(); }}>Regenerate</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
} 